import { inject, Injectable } from "@angular/core";
import { TABLE_SCOPE } from "./tokens/table-scope.token";
import { TABLE_DEFAULT_CONFIG } from "./tokens/table-default-config.token";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { catchError, map, MonoTypeOperatorFunction, Observable, of, shareReplay, startWith, Subject, switchMap, tap } from "rxjs";
import { mergeTableConfig, parseToTableConfigDto, TableConfig, TableConfigDto } from "@shared/components/table-implementation/types/table-config";

/**
 * Provide together with the {@link TABLE_SCOPE | TABLE_SCOPE} injection token and the TableConfigService as well as a {@link TABLE_DEFAULT_CONFIG | Default-Config}.
 * Prefer using the provideTableConfigService-function instead.
 */
@Injectable()
export class TableConfigStoreService {
  private readonly _scope: string = inject(TABLE_SCOPE);

  private readonly _baseDefaultConfig: TableConfig = inject(TABLE_DEFAULT_CONFIG);

  private readonly _httpClient: HttpClient = inject(HttpClient);

  private readonly _baseEndpoint: string = "api/user-config";

  private _configSuccessFullSaved$: Subject<void> = new Subject<void>();


  public configs$: Observable<TableConfig[]> = this._configSuccessFullSaved$.pipe(
    startWith(void 0),
    switchMap(() => this.fetchConfigs()
      .pipe(
        map((configs: TableConfigDto[]) => {
          if (configs.length === 0) return [ structuredClone(this._baseDefaultConfig) ];

          const preparedConfigs: TableConfig[] = configs
            .map((config: TableConfigDto) => mergeTableConfig(config, structuredClone(this._baseDefaultConfig)).mergedConfig)
            .filter((config: TableConfig | undefined): config is TableConfig => config !== undefined)
            .map((config: TableConfig) => structuredClone(config));


          if (!preparedConfigs.some((config: TableConfig) => config.isDefault))
            preparedConfigs.push(structuredClone(this._baseDefaultConfig));

          return preparedConfigs;
        })
      )),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public defaultConfig$: Observable<TableConfig> = this.configs$.pipe(
    map((configs: TableConfig[]) => {
      const config: TableConfig | undefined = configs.find((c: TableConfig) => c.isDefault);

      if (!config)
        return this._baseDefaultConfig;

      return config;
    })
  );

  public favoriteConfigs$: Observable<TableConfig[]> = this.configs$.pipe(map((configs: TableConfig[]) => configs.filter((config: TableConfig) => config.isFavorite && !config.isDefault)));


  constructor () {
    if (!this._scope)
      throw new Error("No Table-Config-Scope provided - Scope may not be an empty string!");
  }

  private refreshAfterResp<T> (): MonoTypeOperatorFunction<T> {
    return tap({
      finalize: () => {
        this._configSuccessFullSaved$.next();
      }
    });
  }

  public create (configName: string, createFrom: TableConfig): Observable<HttpResponse<void>> {
    const configDto: TableConfigDto = parseToTableConfigDto(createFrom);

    configDto.defaultConfig = false;
    configDto.favourite = false;
    configDto.configName = configName;

    return this._httpClient.post<undefined>(`${ this._baseEndpoint }/${ this._scope }/${ configName }`, configDto, { observe: "response" }).pipe(this.refreshAfterResp());
  }

  public update (id: string, patch: TableConfig): Observable<HttpResponse<void>> {
    const configDto: TableConfigDto = parseToTableConfigDto(patch);

    configDto.defaultConfig = false;

    return this._httpClient.put<undefined>(`${ this._baseEndpoint }/${ this._scope }/${ id }`, configDto, { observe: "response" }).pipe(this.refreshAfterResp());
  }


  public delete (id: string): Observable<HttpResponse<void>> {
    return this._httpClient[ "delete" ]<undefined>(`${ this._baseEndpoint }/${ this._scope }/${ id }`, { observe: "response" })
      .pipe(
        this.refreshAfterResp()
      );
  }


  private fetchConfigs (): Observable<TableConfigDto[]> {
    return this._httpClient.get<TableConfigDto[]>(`${ this._baseEndpoint }/${ this._scope }/configs`, { observe: "response" })
      .pipe(
        map((resp: HttpResponse<TableConfigDto[]>) => resp.body ?? []),
        catchError(() => of([]))
      );
  }
}
